import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Box,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as DownloadIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config/config';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
  searchContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
  searchInput: {
    flexGrow: 1,
  },
  addButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  downloadButton: {
    backgroundColor: '#2196f3',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1976d2',
    },
  },
  generateButton: {
    backgroundColor: '#ff9800',
    color: 'white',
    '&:hover': {
      backgroundColor: '#f57c00',
    },
  },
  urlLabel: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    border: '1px solid #e0e0e0',
  },
  dialogContent: {
    minWidth: 500,
  },
}));

const api = axios.create({
  baseURL: config.API.BASE_URL,
  timeout: config.API.TIMEOUT,
});

const UsersFlagsPage = () => {
  const classes = useStyles();
  const [userFlags, setUserFlags] = useState([]);
  const [filteredFlags, setFilteredFlags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFlag, setEditingFlag] = useState(null);
  const [formData, setFormData] = useState({
    user: '',
    validity: '',
    trainer_replicate: '',
    description: '',
    computer_id: ''
  });
  const [dateFields, setDateFields] = useState({
    year: '',
    month: '',
    day: ''
  });

  useEffect(() => {
    fetchUserFlags();
  }, []);

  useEffect(() => {
    // Filter flags based on search term
    if (searchTerm.trim() === '') {
      setFilteredFlags(userFlags);
    } else {
      const filtered = userFlags.filter((flag) =>
        flag.user?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        flag.validity?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        flag.trainer_replicate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        flag.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        flag.computer_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredFlags(filtered);
    }
    setPage(0);
  }, [userFlags, searchTerm]);

  const fetchUserFlags = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await api.get('/user_flags');
      if (response.data.status) {
        setUserFlags(response.data.userFlags || []);
      } else {
        toast.error('Error loading user flags');
      }
    } catch (error) {
      console.error('Error fetching user flags:', error);
      toast.error('Error loading user flags');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRefresh = () => {
    fetchUserFlags();
  };

  const handleOpenDialog = (flag = null) => {
    if (flag) {
      setEditingFlag(flag);
      setFormData({
        user: flag.user || '',
        validity: flag.validity || '',
        trainer_replicate: flag.trainer_replicate || '0',
        description: flag.description || '',
        computer_id: flag.computer_id || ''
      });
      // Parse validity date if it exists
      if (flag.validity && flag.validity.includes('-')) {
        const [year, month, day] = flag.validity.split('-');
        setDateFields({ year, month, day });
      } else {
        setDateFields({ year: '', month: '', day: '' });
      }
    } else {
      setEditingFlag(null);
      setFormData({
        user: '',
        validity: '',
        trainer_replicate: '0',
        description: '',
        computer_id: ''
      });
      setDateFields({ year: '', month: '', day: '' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingFlag(null);
    setFormData({
      user: '',
      validity: '',
      trainer_replicate: '0',
      description: '',
      computer_id: ''
    });
    setDateFields({ year: '', month: '', day: '' });
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleDateChange = (field) => (event) => {
    const newDateFields = {
      ...dateFields,
      [field]: event.target.value
    };
    setDateFields(newDateFields);
    
    // Update validity in formData if all date fields are filled
    if (newDateFields.year && newDateFields.month && newDateFields.day) {
      const formattedDate = `${newDateFields.year}-${newDateFields.month}-${newDateFields.day}`;
      setFormData(prev => ({
        ...prev,
        validity: formattedDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        validity: ''
      }));
    }
  };

  const handleCheckboxChange = (event) => {
    setFormData(prev => ({
      ...prev,
      trainer_replicate: event.target.checked ? '1' : '0'
    }));
  };

  // Generate year options (current year - 10 to current year + 10)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let i = currentYear - 10; i <= currentYear + 10; i++) {
    yearOptions.push(i);
  }

  // Generate month options with leading zeros
  const monthOptions = [
    { value: '01', label: '01' },
    { value: '02', label: '02' },
    { value: '03', label: '03' },
    { value: '04', label: '04' },
    { value: '05', label: '05' },
    { value: '06', label: '06' },
    { value: '07', label: '07' },
    { value: '08', label: '08' },
    { value: '09', label: '09' },
    { value: '10', label: '10' },
    { value: '11', label: '11' },
    { value: '12', label: '12' }
  ];

  // Generate day options with leading zeros
  const dayOptions = [];
  for (let i = 1; i <= 31; i++) {
    const dayValue = i.toString().padStart(2, '0');
    dayOptions.push({ value: dayValue, label: dayValue });
  }

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      let response;
      if (editingFlag) {
        response = await api.put(`/user_flags/${editingFlag.dbid}`, formData);
      } else {
        response = await api.post('/user_flags', formData);
      }
      
      if (response.data.status) {
        toast.success(`User flag ${editingFlag ? 'updated' : 'created'} successfully`);
        handleCloseDialog();
        fetchUserFlags();
      } else {
        toast.error(response.data.message || 'Error saving user flag');
      }
    } catch (error) {
      console.error('Error saving user flag:', error);
      toast.error('Error saving user flag');
    }
  };

  const handleDelete = async (flagId) => {
    if (!window.confirm('Are you sure you want to delete this user flag?')) {
      return;
    }

    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await api.delete(`/user_flags/${flagId}`);
      if (response.data.status) {
        toast.success('User flag deleted successfully');
        fetchUserFlags();
      } else {
        toast.error('Error deleting user flag');
      }
    } catch (error) {
      console.error('Error deleting user flag:', error);
      toast.error('Error deleting user flag');
    }
  };

  const handleGenerateConfig = async () => {
    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await api.get('/config/generate');
      if (response.data.status) {
        toast.success('Configuration file generated successfully');
      } else {
        toast.error('Error generating configuration file');
      }
    } catch (error) {
      console.error('Error generating config:', error);
      toast.error('Error generating configuration file');
    }
  };

  const handleDownloadConfig = async () => {
    try {
      const token = localStorage.getItem(config.SECURITY.TOKEN_KEY);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await api.get('/config/generate');
      if (response.data.status && response.data.fileUrl) {
        // Create download link
        const link = document.createElement('a');
        link.href = response.data.fileUrl;
        link.download = 'users_flags.cse';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Configuration file generated and downloaded successfully');
      } else {
        toast.error('Error generating configuration file');
      }
    } catch (error) {
      console.error('Error generating config:', error);
      toast.error('Error generating configuration file');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedFlags = filteredFlags.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        User Flags Management
      </Typography>
      
      <Box className={classes.urlLabel}>
        <Typography variant="body2" color="textSecondary">
          <strong>Generate from browser:</strong> GET {config.API.BASE_URL}/config/generate
        </Typography>
      </Box>

      <Paper className={classes.paper}>
        {/* Search and Actions */}
        <Box className={classes.searchContainer}>
          <TextField
            className={classes.searchInput}
            variant="outlined"
            size="small"
            placeholder="Search user flags..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon color="action" />,
            }}
          />
          <Button
            variant="contained"
            className={classes.addButton}
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Flag
          </Button>
          <Button
            variant="contained"
            className={classes.generateButton}
            onClick={handleGenerateConfig}
          >
            GENERATE CSE
          </Button>
          <Button
            variant="contained"
            className={classes.downloadButton}
            startIcon={<DownloadIcon />}
            onClick={handleDownloadConfig}
          >
            Download Config
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Flags Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Validity</TableCell>
                <TableCell>Trainer Replicate</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Computer ID</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading user flags...
                  </TableCell>
                </TableRow>
              ) : paginatedFlags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No user flags found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedFlags.map((flag) => (
                  <TableRow key={flag.dbid}>
                    <TableCell>{flag.dbid}</TableCell>
                    <TableCell>{flag.user || 'N/A'}</TableCell>
                    <TableCell>{flag.validity || 'N/A'}</TableCell>
                    <TableCell>
                      {flag.trainer_replicate === '1' ? (
                        <Chip label="Enabled" color="primary" size="small" />
                      ) : (
                        <Chip label="Disabled" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{flag.description || 'N/A'}</TableCell>
                    <TableCell>{flag.computer_id || 'N/A'}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(flag)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(flag.dbid)}
                          color="secondary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredFlags.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFlag ? 'Edit User Flag' : 'Add New User Flag'}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="User"
                value={formData.user}
                onChange={handleInputChange('user')}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Validity Date
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={dateFields.year}
                      onChange={handleDateChange('year')}
                      label="Year"
                    >
                      {yearOptions.map(year => (
                        <MenuItem key={year} value={year.toString()}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={dateFields.month}
                      onChange={handleDateChange('month')}
                      label="Month"
                    >
                      {monthOptions.map(month => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Day</InputLabel>
                    <Select
                      value={dateFields.day}
                      onChange={handleDateChange('day')}
                      label="Day"
                    >
                      {dayOptions.map(day => (
                        <MenuItem key={day.value} value={day.value}>
                          {day.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {formData.validity && (
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                  Formatted date: {formData.validity}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.trainer_replicate === '1'}
                    onChange={handleCheckboxChange}
                    color="primary"
                  />
                }
                label="Trainer Replicate (Enable AI Training)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleInputChange('description')}
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Computer ID"
                value={formData.computer_id}
                onChange={handleInputChange('computer_id')}
                variant="outlined"
                placeholder="Enter computer identifier"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {editingFlag ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UsersFlagsPage;